
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTasks } from './TaskContext';
import { useActiveTimeEntry } from '@/hooks/timer/useActiveTimeEntry';

import { timerReducer, initialTimerState } from '@/hooks/timer/timerReducer';
import { TimerContextType, TimerSettings } from '@/hooks/timer/timerTypes';
import { handleTimerCompletion } from '@/hooks/timer/timerCompletion';
import { useDocumentTitle } from '@/hooks/timer/useDocumentTitle';
import { 
  createStartAction, 
  createStopAction, 
  createResetAction, 
  createTickAction, 
  createCompleteAction,
  createUpdateSettingsAction,
  createChangeTypeAction 
} from '@/hooks/timer/timerActionCreators';

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(timerReducer, initialTimerState);
  const { user } = useAuth();
  const { activeTask, updateTaskTime } = useTasks();
  const navigate = useNavigate();
  const { 
    startTimeEntry, 
    finalizeCurrentEntry, 
    isTracking,
    updateCurrentEntry 
  } = useActiveTimeEntry();

  useDocumentTitle(state.minutes * 60 + state.seconds, state.timerType);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (state.isRunning && (state.minutes > 0 || state.seconds > 0)) {
      interval = setInterval(() => {
        dispatch(createTickAction());
      }, 1000);
    } else if (state.isRunning && state.minutes === 0 && state.seconds === 0) {
      handleCompletion();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [state.isRunning, state.minutes, state.seconds]);

  const handleCompletion = useCallback(async () => {
    const newCompletedPomodoros = await handleTimerCompletion({
      timerType: state.timerType,
      sequencePosition: state.sequencePosition,
      completedPomodoros: state.completedPomodoros,
      settings: state.settings,
      activeTask,
      updateTaskTime,
      resetTimer: (type, startRunning, sequencePos) => {
        dispatch(createResetAction(type, startRunning, sequencePos));
      },
      setIsFirstStart: () => {},
      setSessionIndex: () => {},
      user,
      finalizeCurrentEntry
    });

    dispatch(createCompleteAction(newCompletedPomodoros));
  }, [state, activeTask, updateTaskTime, user, finalizeCurrentEntry]);

  useEffect(() => {
    const handleStartFocusTime = async () => {
      try {
        if (state.timerType !== 'pomodoro') {
          dispatch(createChangeTypeAction('pomodoro'));
        }
        dispatch(createResetAction('pomodoro', true));
        navigate('/');
      } catch (error) {
        console.error("TimerContext: Error starting focus time:", error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('startFocusTime', handleStartFocusTime);
      return () => {
        window.removeEventListener('startFocusTime', handleStartFocusTime);
      };
    }
  }, [state.timerType, navigate]);

  const toggleTimer = useCallback(async () => {
    if (state.isRunning) {
      dispatch(createStopAction());
      if (isTracking) {
        await updateCurrentEntry(true);
      }
    } else {
      dispatch(createStartAction());
      if (state.timerType === 'pomodoro' && user && activeTask && !isTracking) {
        try {
          await startTimeEntry();
        } catch (error) {
          console.error('Failed to start time tracking:', error);
        }
      }
    }
  }, [state.isRunning, state.timerType, user, activeTask, isTracking, startTimeEntry, updateCurrentEntry]);

  const resetTimer = useCallback(async (type = state.timerType, startRunning = false, sequencePos = 0) => {
    if (isTracking && state.isRunning) {
      try {
        await finalizeCurrentEntry('interrupted');
      } catch (error) {
        console.error('Failed to finalize interrupted session:', error);
      }
    }
    dispatch(createResetAction(type, startRunning, sequencePos));
  }, [state.timerType, state.isRunning, isTracking, finalizeCurrentEntry]);

  const changeTimerType = useCallback(async (type: string) => {
    if (isTracking && state.isRunning && state.timerType === 'pomodoro') {
      try {
        await finalizeCurrentEntry('interrupted');
      } catch (error) {
        console.error('Failed to finalize session on type change:', error);
      }
    }
    dispatch(createChangeTypeAction(type as any));
  }, [state.isRunning, state.timerType, isTracking, finalizeCurrentEntry]);

  const saveSettings = useCallback((newSettings: TimerSettings) => {
    dispatch(createUpdateSettingsAction(newSettings));
  }, []);

  const goToPrevious = useCallback(() => {
    const newPos = Math.max(0, state.sequencePosition - 1);
    const newType = newPos % 2 === 0 ? 'pomodoro' : 
                   (state.completedPomodoros % state.settings.sessions === 0 && newPos === state.settings.sessions * 2 - 1) ? 
                   'longBreak' : 'shortBreak';
    resetTimer(newType, false, newPos);
  }, [state.sequencePosition, state.completedPomodoros, state.settings.sessions, resetTimer]);

  const goToNext = useCallback(() => {
    const maxPos = state.settings.sessions * 2 - 1;
    const newPos = Math.min(maxPos, state.sequencePosition + 1);
    const newType = newPos % 2 === 0 ? 'pomodoro' : 
                   (newPos === maxPos) ? 'longBreak' : 'shortBreak';
    resetTimer(newType, false, newPos);
  }, [state.sequencePosition, state.settings.sessions, resetTimer]);

  const currentSessionIndex = Math.floor(state.sequencePosition / 2);

  const contextValue: TimerContextType = {
    minutes: state.minutes,
    seconds: state.seconds,
    isRunning: state.isRunning,
    timerType: state.timerType,
    settings: state.settings,
    completedPomodoros: state.completedPomodoros,
    sequencePosition: state.sequencePosition,
    currentSessionIndex,
    progress: state.progress,
    timerTitle: state.timerTitle,
    toggleTimer,
    resetTimer,
    changeTimerType,
    saveSettings,
    goToPrevious,
    goToNext
  };

  return (
    <TimerContext.Provider value={contextValue}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimerContext = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimerContext must be used within a TimerProvider');
  }
  return { timer: context };
};
